import React from 'react';
import { PlacedEquipment } from '../../types';
import { useProjectStore } from '../../store/projectStore';
import { DEFAULT_PIXELS_PER_METER, formatMeters } from '../../utils/floorPlanGeometry';
import { canvasIconSizePx } from '../../utils/canvasIcon';

interface APRangeVisualizationProps {
  equipment: PlacedEquipment[];
  showRange: boolean;
}

/** Parse coverage string like "150m", "500 ft", "30" into meters */
function parseCoverageMeters(coverage: unknown, equipmentName: string): number {
  if (typeof coverage === 'number' && Number.isFinite(coverage)) {
    return coverage;
  }
  if (typeof coverage === 'string') {
    const num = parseFloat(coverage.replace(/,/g, '').match(/[\d.]+/)?.[0] || '');
    if (!Number.isFinite(num)) {
      return equipmentName.toLowerCase().includes('outdoor') ? 100 : 20;
    }
    const lower = coverage.toLowerCase();
    if (lower.includes('ft') || lower.includes("'")) {
      return num * 0.3048;
    }
    if (lower.includes('km')) {
      return num * 1000;
    }
    // Assume meters (or bare number as meters for outdoor, as feet historically — prefer m)
    return num;
  }
  return equipmentName.toLowerCase().includes('outdoor') ? 100 : 20;
}

const APRangeVisualization: React.FC<APRangeVisualizationProps> = ({ equipment, showRange }) => {
  const getFloorPlan = useProjectStore((s) => s.getFloorPlan);
  const gridSize = useProjectStore((s) => s.gridSize);
  const iconGridCells = useProjectStore((s) => s.iconGridCells);
  const iconHalf = canvasIconSizePx(gridSize, iconGridCells) / 2;
  const floorPlan = getFloorPlan();
  const pixelsPerMeter = DEFAULT_PIXELS_PER_METER;
  const units = floorPlan?.units || 'metric';
  if (!showRange) return null;

  return (
    <>
      {equipment
        .filter((eq) => eq.equipment.type === 'ap')
        .map((ap) => {
          const coverage = ap.equipment.specifications?.coverage;
          const frequency = ap.equipment.specifications?.frequency || '2.4GHz';
          const isOutdoor =
            ap.equipment.name.toLowerCase().includes('outdoor') ||
            ap.equipment.name.toLowerCase().includes('mesh');

          let coverageMeters = parseCoverageMeters(coverage, ap.equipment.name);

          // Frequency adjustment (5/6 GHz shorter range)
          if (
            typeof frequency === 'string' &&
            (frequency.includes('5G') || frequency.includes('6G'))
          ) {
            coverageMeters *= 0.8;
          }
          // Indoor heuristic: slightly reduce marketed outdoor-style numbers
          if (!isOutdoor) {
            coverageMeters *= 0.85;
          }

          const maxRange = coverageMeters * pixelsPerMeter;
          const strongSignalRange = maxRange * 0.65;
          const weakSignalRange = maxRange * 0.85;

          const centerX = ap.position.x + iconHalf;
          const centerY = ap.position.y + iconHalf;
          const label = formatMeters(coverageMeters, units, 0);

          return (
            <div
              key={ap.id}
              className="absolute pointer-events-none"
              style={{
                left: centerX - maxRange,
                top: centerY - maxRange,
                width: maxRange * 2,
                height: maxRange * 2,
                zIndex: 1,
              }}
            >
              <div
                className="absolute rounded-full"
                style={{
                  width: '100%',
                  height: '100%',
                  background:
                    'radial-gradient(circle, rgba(59, 130, 246, 0.06) 0%, rgba(59, 130, 246, 0.03) 70%, rgba(59, 130, 246, 0.01) 100%)',
                  border: '1px dashed rgba(59, 130, 246, 0.2)',
                  animation: 'apRangeSubtle 6s ease-in-out infinite 2s',
                }}
              />

              <div
                className="absolute rounded-full"
                style={{
                  width: `${(weakSignalRange / maxRange) * 100}%`,
                  height: `${(weakSignalRange / maxRange) * 100}%`,
                  top: `${((maxRange - weakSignalRange) / maxRange) * 50}%`,
                  left: `${((maxRange - weakSignalRange) / maxRange) * 50}%`,
                  background:
                    'radial-gradient(circle, rgba(59, 130, 246, 0.08) 0%, rgba(59, 130, 246, 0.04) 80%, rgba(59, 130, 246, 0.02) 100%)',
                  border: '1px solid rgba(59, 130, 246, 0.25)',
                  animation: 'apRangeSubtle 6s ease-in-out infinite 1s',
                }}
              />

              <div
                className="absolute rounded-full"
                style={{
                  width: `${(strongSignalRange / maxRange) * 100}%`,
                  height: `${(strongSignalRange / maxRange) * 100}%`,
                  top: `${((maxRange - strongSignalRange) / maxRange) * 50}%`,
                  left: `${((maxRange - strongSignalRange) / maxRange) * 50}%`,
                  background:
                    'radial-gradient(circle, rgba(59, 130, 246, 0.12) 0%, rgba(59, 130, 246, 0.06) 70%, rgba(59, 130, 246, 0.03) 100%)',
                  border: '1px solid rgba(59, 130, 246, 0.3)',
                  animation: 'apRangeSubtle 6s ease-in-out infinite',
                }}
              />

              <div
                className="absolute bg-slate-800/80 text-white text-xs px-2 py-1 rounded shadow-lg border border-slate-600/50"
                style={{
                  bottom: `-15px`,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  whiteSpace: 'nowrap',
                }}
              >
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 rounded-full bg-blue-400" />
                  <span>{label}</span>
                  <span className="text-gray-400">• {String(frequency)}</span>
                </div>
              </div>
            </div>
          );
        })}
    </>
  );
};

export default APRangeVisualization;
